import { OnDevicesAPISubscription } from 'ngx-core-services';
import { decodeMultiQuery, encodeMultiQuery, fromHexStringToUint8Array, ModbusLink, INTERNET_RETRY_POLICY } from 'ngx-modbus';
import { BehaviorSubject, from, Subject, Subscription } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { ViewportScroller } from '@angular/common';
import { Router } from '@angular/router';
import { ToastService, ErrorSinkService } from 'ngx-zcs-components';
import {
  AppsyncModbusResponse,
  CallAPISolarmanDeviceInfoResponse,
  SolarmanDeviceStatus,
  CommunicationChannel,
  TypeInternetDevice,
} from 'ngx-modbus';

import { SolarmanService } from './solarman.service';
import { solarmanModbusResponse } from './graphql-fragments';
import { resolveInverterVersion } from './utils';

const INET_DEBUG = { ENABLED: true };
const inetLog = (msg: string, data?: unknown) => INET_DEBUG.ENABLED && console.log(`[Modbus:Internet] ${msg}`, data ?? '');

const INTERNET_TIMEOUT = 10 * 60 * 1000;

/**
 * Reusable, non-singleton implementation of CommunicationChannel for Internet (Solarman) devices.
 * Multiple instances can coexist — one per device — each with isolated state (command, subscriptions, etc.).
 * Used by InternetChannelFactory to create per-device channels.
 * InternetService extends this class for retrocompat as a singleton.
 */
export class InternetChannelInstance implements CommunicationChannel {
  readonly connectionStatus$ = new BehaviorSubject<boolean>(false);
  readonly response$ = new Subject<Uint8Array>();
  readonly responseByAppsync$ = new Subject<AppsyncModbusResponse>();
  private responseSubscription$?: Subscription;
  private deviceParams?: CallAPISolarmanDeviceInfoResponse;
  command?: ModbusLink | undefined;
  internetDevice?: TypeInternetDevice;
  refreshByDefault = 20 * 60 * 1000;

  concurrentQuery = {
    count: 256,
    encodeMultiQuery,
    decodeMultiQuery,
  };

  get isConnected(): boolean {
    // Device State Value:1. Online 2. Alerting 3. Offline
    const isConnected = [
      SolarmanDeviceStatus.Online,
      SolarmanDeviceStatus.Alarm,
    ].includes(this.deviceParams?.communication.deviceState || SolarmanDeviceStatus.Offline);
    this.connectionStatus$.next(isConnected);
    return isConnected;
  }

  get deviceId() {
    if (this.internetDevice?.serial) {
      return this.internetDevice?.serial;
    }
    return (this.deviceParams?.communication.childList?.length || 0) > 1
      ? this.deviceParams?.communication.childList.filter((x) => x.deviceSn.length === 20)[0].deviceSn
      : this.deviceParams?.communication.childList?.[0].deviceSn;
  }

  get loggerId(): number | undefined {
    return this.deviceParams?.communication.childList[0].deviceId || this.internetDevice?.loggerId;
  }

  get inverterId() {
    if (this.internetDevice?.serial) {
      return this.internetDevice?.serial;
    }
    const deviceIds = this.deviceParams?.communication.childList.filter((x) => x.deviceType === 'INVERTER');
    /**
     * If there are more than one inverter, select the first one with deviceId length 20 (inverter)
     * otherwise select the first one
     */
    return (deviceIds?.length || 0) > 1
      ? deviceIds?.filter((x) => x.deviceSn.length === 20)[0].deviceSn
      : deviceIds?.[0].deviceSn;
  }

  readonly scan = async () => { throw new Error('Method not implemented.'); };

  constructor(
    protected readonly onDevicesAPISubscription: OnDevicesAPISubscription,
    protected readonly router: Router,
    protected readonly solarmanService: SolarmanService,
    protected readonly toastService: ToastService,
    protected readonly viewportScroller: ViewportScroller,
    protected readonly errorSink: ErrorSinkService,
  ) {
    this.interaction = this.interaction.bind(this);
    this.close = this.close.bind(this);
  }

  async close(): Promise<void> {
    inetLog(` close() deviceId="${this.deviceId}"`);
    if (this.responseSubscription$) {
      this.responseSubscription$.unsubscribe();
    }

    this.command?.destroy();

    this.connectionStatus$.next(false);
    this.connectionStatus$.complete();
    this.response$.complete();
    this.responseByAppsync$.complete();

    this.deviceParams = undefined;
    this.command = undefined;
  }

  async interaction(cmd: Uint8Array): Promise<Uint8Array> {
    if (!this.deviceId) {
      throw new Error('Device not found');
    }

    const request = await this.solarmanService.deviceModbusCmdRequest(cmd, this.deviceId);
    if (request.success === false) {
      throw new Error('Request failed');
    }

    const response = await this.responseByAppsync$.pipe(
      filter((r) => r.orderId === request.orderId),
      first(),
    ).toPromise();

    const responseAsUint = fromHexStringToUint8Array(response.ack);
    inetLog(`RECEIVE - 0x${Array.from(responseAsUint).map((x) => x.toString(16).padStart(2, '0')).join(' ')}`);
    this.response$.next(responseAsUint);
    return responseAsUint;
  }

  async openByInverterSerial(serial: string): Promise<void> {
    inetLog(` openByInverterSerial("${serial}")`);
    try {
      this.deviceParams = await this.solarmanService.deviceInfoRequest(serial);
      if (this.deviceParams?.communication.childList === null) {
        throw new Error('Logger not found');
      }
    } catch (error) {
      inetLog('openByInverterSerial error:', error);
      try {
        await this.solarmanService.addLoggerToSolarmanByInverterSerial(serial);
        this.deviceParams = await this.solarmanService.deviceInfoRequest(serial);
      } catch {
        await this.toastService.persistence('Logger not found', 'Error');
        throw new Error('Logger not found');
      }
    }

    if (!this.isConnected) {
      await this.toastService.persistence('Logger is offline', 'Error');
      throw new Error('Logger is offline');
    }

    const deviceIds = this.deviceParams?.communication.childList
      .filter((x) => x.deviceType === 'INVERTER')
      .filter((x) => serial === x.deviceSn)
      .filter((x) => [
        SolarmanDeviceStatus.Online,
        SolarmanDeviceStatus.Alarm,
      ].includes(x.deviceState));

    /**
     * If there are more than one inverter, select the first one with deviceId length 20 (inverter)
     * otherwise select the first one
     */
    const selectedDevice = deviceIds?.length > 1
      ? deviceIds.filter((x) => x.deviceSn.length === 20)?.[0]
      : deviceIds?.[0];

    const deviceId = selectedDevice?.deviceId;
    const loggerSn = selectedDevice.parentSn;

    if (deviceId && loggerSn) {
      this.responseSubscription$ = from(await this.onDevicesAPISubscription.execute(solarmanModbusResponse, {
        deviceId,
      }).pipe(first()).toPromise() as any).subscribe((result: any) => {
        const data = (result).data.onDevicesAPI as AppsyncModbusResponse;
        this.responseByAppsync$.next(data);
      });

      const handleFatalError = async () => {
        this.errorSink.reportTransport(
          'Sorry, something went wrong',
          `Fatal error on channel for device ${serial}`,
        );
        await this.close();
        if (this.internetDevice?.onFatalError) {
          this.internetDevice.onFatalError();
          return;
        }
        await this.router.navigate(['/']).then(() => {
          this.viewportScroller.scrollToPosition([0, 0]);
        });
      };

      const inverterVersion = resolveInverterVersion(serial);
      inetLog(` ModbusLink: serial="${serial}", inverterVersion=${inverterVersion ?? 'legacy'}`);

      this.command = new ModbusLink({
        deviceId: serial,
        inverterVersion,
        concurrentQuery: this.concurrentQuery,
        executeCmd: this.interaction,
        maxTimeoutInMs: INTERNET_TIMEOUT,
        modbusAddress: 1,
        handleFatalError,
        retryPolicy: INTERNET_RETRY_POLICY,
      });
      this.command.reset();
      inetLog(` Opened successfully for serial="${serial}", ModbusLink created`);
    }
  }

  async getInverterSerialFromLogger(loggerId: string): Promise<string> {
    let deviceParams;

    try {
      deviceParams = await this.solarmanService.deviceInfoRequest(`${loggerId}`);
      if (!deviceParams?.communication.childList?.length) {
        throw new Error('Logger not found');
      }
    } catch (error) {
      try {
        await this.solarmanService.addLoggerToSolarmanByLoggerSerial(loggerId);
        deviceParams = await this.solarmanService.deviceInfoRequest(`${loggerId}`);
        if (!deviceParams?.communication.childList?.length) {
          throw new Error('Logger not found');
        }
      } catch {
        await this.toastService.persistence('Logger not found', 'Error');
        throw new Error('Logger not found');
      }
    }

    return deviceParams.communication.childList[0].deviceSn;
  }

  async open(internetDevice: TypeInternetDevice): Promise<void> {
    inetLog(` open() serial="${internetDevice.serial}" loggerId="${internetDevice.loggerId}"`);
    this.internetDevice = internetDevice;
    await this.solarmanService.authenticate();

    this.command?.destroy();

    if (this.responseSubscription$) {
      this.responseSubscription$.unsubscribe();
    }

    if (internetDevice.serial) {
      await this.openByInverterSerial(internetDevice.serial);
    } else if (internetDevice.loggerId) {

      const inverterSerial = await this.getInverterSerialFromLogger(`${internetDevice.loggerId}`);
      if (inverterSerial) {
        await this.openByInverterSerial(inverterSerial);
      }
    }
  }
}
